import {
  ActionIcon,
  Button,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  SimpleGrid,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import { customLoader } from "components/utilities/loader";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Plus, Search, X } from "tabler-icons-react";
import { formatNumber, PAGE_SIZE_OPTIONS } from "utiles/url";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
    paddingRight: "50px", // Add padding to avoid text overlap with button
  },
  searchButton: {
    position: "absolute",
    right: 0,
    borderRadius: "0 4px 4px 0",
    height: "70%",
    width: "40px", // Fixed width for the button
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    cursor: "pointer",
  },
}));

function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group position="apart">
          <Text weight={500} size="sm">
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon size={14} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  );
}

function filterData(data, search) {
  const query = search?.toLowerCase().trim();
  return data.filter((item) => {
    return Object.keys(item).some((key) => {
      const value = item[key];
      if (typeof value === "string") {
        return value.toLowerCase().includes(query);
      }
      if (key === "causer" && value && typeof value === "object") {
        return value.name?.toLowerCase().includes(query);
      }
      if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (val) => typeof val === "string" && val.toLowerCase().includes(query),
        );
      }
      return false;
    });
  });
}

function sortData(data, payload) {
  if (!payload.sortBy) {
    return payload.search ? filterData(data, payload.search) : data;
  }
  const sorted = [...data].sort((a, b) => {
    let aValue = a[payload.sortBy];
    let bValue = b[payload.sortBy];
    if (payload.sortBy === "name_user") {
      aValue = a.user?.name || "";
      bValue = b.user?.name || "";
    }
    if (payload.sortBy === "causer") {
      aValue = a.causer?.name || "";
      bValue = b.causer?.name || "";
    }
    if (payload.sortBy === "created_at") {
      const aDate = aValue ? new Date(aValue) : new Date(0);
      const bDate = bValue ? new Date(bValue) : new Date(0);
      return payload.reversed ? bDate - aDate : aDate - bDate;
    }
    const aStr = aValue !== null && aValue !== undefined ? String(aValue) : "";
    const bStr = bValue !== null && bValue !== undefined ? String(bValue) : "";
    if (payload.sortBy === "cost" || payload.sortBy === "total_price") {
      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);
      return payload.reversed ? bNum - aNum : aNum - bNum;
    } else {
      return payload.reversed
        ? bStr.localeCompare(aStr)
        : aStr.localeCompare(bStr);
    }
  });
  return payload.search ? filterData(sorted, payload.search) : sorted;
}

const B2bTable = ({
  data,
  header,
  loading,
  optionsData,
  total,
  searchInput,
  content,
  activePage,
  handleChange,
  collapsible,
  selectedCollapse,
  setSelectedCollapse,
  size,
  handlePageSizeChange,
  filterData,
  clearFilter,
  dropoffStatus,
  handelSearch,
  clearInput,
  searchValue,
  onSearchChange,
  layout,
  paginatorInfo,
}) => {
  const { classes } = useStyles();
  const [localSearch, setLocalSearch] = useState("");
  const [sortedData, setSortedData] = useState(data);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  // if a header entry with key "index" already exists we leave header alone
  const computedHeader = useMemo(() => {
    if (!header) return [];
    if (header.some((h) => h.key === "index")) {
      return header;
    }
    return [
      {
        label: "#",
        key: "index",
        sortable: false,
        searchable: false,
        width: "5%",
        render: (_rowData, rowIndex) => {
          const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
          return <span>{idx}</span>;
        },
      },
      ...header,
    ];
  }, [header, activePage, size]);

  const handleClearSearch = () => {
    setLocalSearch("");
    clearInput();
  };

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(
      sortData(data, { sortBy: field, reversed, search: searchValue }),
    );
  };

  const handleSearchChange = () => {
    handelSearch(searchValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form submission or other default behavior
      handleSearchChange();
    }
  };

  const rows = sortedData.map((row, rowIndex) => (
    <Fragment key={row.id}>
      <tr>
        {computedHeader.map((data, index) => (
          <td
            key={index}
            style={{ width: data.width || `${100 / computedHeader.length}%` }}
          >
            {data.render(row, rowIndex)}
          </td>
        ))}
      </tr>
      {row.id === selectedCollapse && collapsible && content(row)}
    </Fragment>
  ));

  useEffect(() => {
    setSortedData(data);
  }, [data]);

  const handleCategoryFilterClick = (categoryId) => {};

  return (
    <ScrollArea type="never">
      <SimpleGrid cols={3}>
        {optionsData ? (
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              onClick={() => optionsData.setAddModal(true)}
              style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
              leftIcon={<Plus size={14} />}
            >
              {optionsData.actionLabel}
            </Button>
            {filterData && (
              <div style={{ flex: 1 }}>
                {filterData({ onCardClick: handleCategoryFilterClick })}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              marginBottom: "10px",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {filterData && (
              <div>
                {filterData({ onCardClick: handleCategoryFilterClick })}
              </div>
            )}
            {clearFilter && dropoffStatus && (
              <Button
                onClick={clearFilter}
                style={{
                  flex: 1,
                  width: "10px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                type="submit"
                color="blue"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
        <div></div>
        {searchInput !== false && (
          <div className={classes.searchContainer}>
            <TextInput
              className={classes.searchInput}
              placeholder="Search"
              mb="md"
              value={searchValue}
              onChange={(event) => onSearchChange(event.currentTarget.value)}
              onKeyDown={handleKeyDown} // Add keydown event handler
              rightSection={
                searchValue && (
                  <UnstyledButton onClick={handleClearSearch}>
                    <X size={16} color="red" />
                  </UnstyledButton>
                )
              }
            />
            <button
              className={classes.searchButton}
              onClick={handleSearchChange}
            >
              <Search size={16} />
            </button>
          </div>
        )}
      </SimpleGrid>
      <Table
        highlightOnHover
        horizontalSpacing="md"
        verticalSpacing="xs"
        sx={{
          ...(layout ? { tableLayout: layout } : { tableLayout: "fixed" }),
          minWidth: "100%",
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={loading}
          color="blue"
          overlayBlur={2}
          loader={customLoader}
        />
        <thead style={{ backgroundColor: "#F1F1F1" }}>
          <tr>
            {computedHeader.map((th, index) => {
              return th.sortable ? (
                <Th
                  key={th.label}
                  sorted={sortBy === th.key}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting(th.key)}
                  style={{
                    width: th.width || `${100 / computedHeader.length}%`,
                  }}
                >
                  <span
                    style={{
                      color: "#666666",
                      fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
                      fontSize: "10px",
                      textTransform: "capitalize",
                      fontWeight: "bold",
                    }}
                  >
                    {th.label}
                  </span>
                </Th>
              ) : (
                <th
                  style={{
                    width: th.width || `${100 / computedHeader.length}%`,
                    color: "#666666",
                    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
                    fontSize: "10px",
                    textTransform: "capitalize",
                    fontWeight: "bold",
                  }}
                  key={th.label}
                >
                  {th.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows
          ) : (
            <tr>
              <td colSpan={header.length}>
                <Text weight={500} align="center">
                  Nothing found
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {size && activePage && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "12px",
            padding: "6px 4px",
            borderTop: "1px solid #e9ecef",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {/* Left: Total Records */}
          <Text size="sm" style={{ color: "#FF6A00" }}>
            {(() => {
              if (paginatorInfo) {
                const { currentPage, perPage, total: tot } = paginatorInfo;
                const from = (currentPage - 1) * perPage + 1;
                const to = Math.min(currentPage * perPage, tot);
                return `Total Records: ${formatNumber(tot)}`;
              }
              const perPage = parseInt(size) || 10;
              const from = (activePage - 1) * perPage + 1;
              const to = activePage * perPage;
              return `Total Records: ${formatNumber(paginatorInfo?.total)}`;
            })()}
          </Text>

          {/* Right: Rows select + navigation */}
          <Group spacing={6} noWrap>
            <Text size="sm" color="dimmed">
              Rows
            </Text>
            <Select
              value={size}
              onChange={handlePageSizeChange}
              data={PAGE_SIZE_OPTIONS}
              style={{ width: 72 }}
              size="xs"
            />

            {/* First page */}
            <ActionIcon
              size="sm"
              variant="default"
              onClick={() => handleChange(1)}
              disabled={activePage <= 1}
              title="First page"
              style={{ fontWeight: 700 }}
            >
              «
            </ActionIcon>

            {/* Previous page */}
            <ActionIcon
              size="sm"
              variant="default"
              onClick={() => handleChange(activePage - 1)}
              disabled={activePage <= 1}
              title="Previous page"
            >
              ‹
            </ActionIcon>

            {/* Current / total */}
            <Text size="sm" style={{ minWidth: 40, textAlign: "center" }}>
              {activePage} / {paginatorInfo ? paginatorInfo.lastPage : total}
            </Text>

            {/* Next page */}
            <ActionIcon
              size="sm"
              variant="default"
              onClick={() => handleChange(activePage + 1)}
              disabled={
                activePage >= (paginatorInfo ? paginatorInfo.lastPage : total)
              }
              title="Next page"
            >
              ›
            </ActionIcon>

            {/* Last page */}
            <ActionIcon
              size="sm"
              variant="default"
              onClick={() =>
                handleChange(paginatorInfo ? paginatorInfo.lastPage : total)
              }
              disabled={
                activePage >= (paginatorInfo ? paginatorInfo.lastPage : total)
              }
              title="Last page"
              style={{ fontWeight: 700 }}
            >
              »
            </ActionIcon>
          </Group>
        </div>
      )}
    </ScrollArea>
  );
};

export default B2bTable;
